import { X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import giftIllustration from "@/assets/home_page/gift.svg";
import { QRCodeModal } from "@/components/loyalty/QRCodeModal";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { shareViaEmail, shareViaWhatsapp } from "@/utils/shareUtils";

interface ReferralModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referralCode?: string;
}

export const ReferralModal = ({
  open,
  onOpenChange,
  referralCode = "",
}: ReferralModalProps) => {
  const { t } = useTranslation();
  const [showQRModal, setShowQRModal] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success(t("home.referral_popup.link_copied"));
  };

  const shareViaWhatsappHandler = () => {
    shareViaWhatsapp(referralCode, "LoyaWin");
  };

  const shareViaEmailHandler = () => {
    shareViaEmail(referralCode, "LoyaWin");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[90vw] sm:max-w-[400px] rounded-3xl p-0 overflow-hidden border-none [&>button]:hidden">
          <div className="relative bg-white p-6 flex flex-col items-center text-center w-full max-w-full overflow-x-hidden">
            {/* Close button - top-right, đúng vị trí Figma */}
            <DialogClose className="absolute right-4 top-4 w-8 h-8 flex items-center justify-center rounded-lg bg-surfaceSpecialSecondaryAlphaBase border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all z-10">
              <X className="h-4 w-4 text-black" />
            </DialogClose>

            <div className="w-36 h-36">
              <img
                src={giftIllustration}
                alt="Gift"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="w-full flex flex-col items-center">
              <h2 className="font-semibold text-[20px] mb-3">
                {t("home.referral_popup.title")}
              </h2>

              <p className="text-body-2 font-regular mb-1">
                {t("home.referral_popup.subtitle")}
              </p>

              {/* Referral Link Box */}
              <div className="w-full border border-gray-100 rounded-2xl p-1 mt-2 mb-4 bg-gray-50 flex items-center justify-between overflow-hidden">
                <div className="flex-1 min-w-0 px-3 py-2">
                  <p className="text-textLowEm text-sm truncate font-regular text-left select-all">
                    {referralCode}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  className="bg-white shadow-sm border border-gray-100 rounded-xl px-4 h-9 font-regular text-textMedEm hover:bg-gray-50 active:scale-95 transition-all flex-shrink-0 ml-1"
                  onClick={copyToClipboard}
                >
                  {t("home.referral_popup.copy_link", "Copy")}
                </Button>
              </div>

              {/* Share Options */}
              <div className="w-full space-y-4">
                <p className="text-black text-body-1 font-medium tracking-wider">
                  {t("home.referral_popup.share_via")}
                </p>

                <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full">
                  {/* Whatsapp */}
                  <ShareOption
                    icon={
                      <div className="bg-[#25D366] p-2 rounded-full flex-shrink-0">
                        <WhatsAppIcon className="fill-white w-5 h-5 md:w-6 md:h-6" />
                      </div>
                    }
                    label="Whatsapp"
                    onClick={shareViaWhatsappHandler}
                  />

                  <ShareOption
                    icon={
                      <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border border-gray-100 rounded-full shadow-sm bg-white flex-shrink-0">
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 sm:w-6 sm:h-6"
                        >
                          <path
                            d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                            fill="#EA4335"
                          />
                        </svg>
                      </div>
                    }
                    label="Gmail"
                    onClick={shareViaEmailHandler}
                  />

                  <ShareOption
                    icon={
                      <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white flex-shrink-0">
                        {referralCode ? (
                          <QRCodeSVG value={referralCode} size={32} />
                        ) : (
                          <div className="w-full h-full bg-gray-100 rounded" />
                        )}
                      </div>
                    }
                    label="QR code"
                    onClick={() => setShowQRModal(true)}
                  />
                </div>
              </div>

              {/* Terms and conditions */}
              <div className="mt-6 flex items-center justify-center w-full">
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[15px] font-normal font-poppins text-center"
                  style={{ color: "#574ADB" }}
                >
                  {t("common.terms_conditions", "Terms and conditions")}
                </a>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <QRCodeModal
        open={showQRModal}
        onOpenChange={setShowQRModal}
        value={referralCode}
        title={t("home.referral_popup.share_via_qr", "Share QR Code")}
      />
    </>
  );
};

const ShareOption = ({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center gap-3 p-4 w-full border border-gray-200 rounded-[24px] hover:bg-gray-50 transition-all active:scale-95"
  >
    <div className="flex items-center justify-center">{icon}</div>
    <span className="text-body-2 font-medium">{label}</span>
  </button>
);

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.296-.769.966-.941 1.165-.175.198-.348.223-.648.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.672-1.62-.922-2.22-.243-.584-.488-.504-.672-.514-.175-.01-.375-.01-.574-.01s-.525.075-.798.372c-.273.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.768-.721 2.016-1.412.247-.691.247-1.288.173-1.412-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .018 5.396.015 12.03c0 2.12.554 4.189 1.605 6.009l-1.706 6.233 6.376-1.671a11.791 11.791 0 005.759 1.505h.005c6.635 0 12.032-5.396 12.035-12.03.001-3.213-1.248-6.233-3.518-8.504z" />
  </svg>
);
